import React, {useContext, useState} from "react";
import {Box, ResponsiveContext, Text} from "grommet";
import styled from "styled-components";
import useSWR from "swr";
import DataTableComponent from "../../components/DataTable";
import DateInput from "../../components/DateInput";
import {NotificationsContext} from "../../components/NotificationsProvider";
import {downloadBlobAsCsvFile} from "../../lib/exportcsv";
import {formatDate, formatddMMyyDate, getTodaysDate} from "../../utils/dates";
import {handleErrors, handleFileDownloadErrors} from "../../utils/errors";
import {formatCurrency} from "../../utils/numbers";
import PayoutsPage from "./index";
import {formatName} from "../../utils/names";
import {AGING_BALANCE_COLOR_MAP} from "../../constants/Colors";
import CustomerTag from "../../components/CustomerTag";
import { useRouter } from "next/router";
import Button from "../../components/Button";
import DropButton from "../../components/DropButton";

const TextAlignRight = styled(Box)`
    text-align: right;
    white-space: nowrap;
`

const AgingBalancePage = (props) => {
    const [selectedDate, setSelectedDate] = useState(new Date());

    const fetcher = (url) => fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    }).then(handleErrors)
        .catch(props.showNotification);

    const {data, error } = useSWR(
        `/api/reports/aging-balance?report_date=${selectedDate.toISOString()}`,
        fetcher,
        { revalidateOnFocus: false }
    );

    return (
        <PayoutsPage title="Aging balance" {...props} hidePayout={true}>
            {data && (<AgingBalanceTable {...data} onDateChange={date => setSelectedDate(date)} date={selectedDate} />)}
        </PayoutsPage>
    );
};

const AgingBalanceColumnName = ({color, name}) => (
    <Box direction="row" align="center" gap="xsmall">
        {/* The colored dots next to column name (e.g. 1-30 days) */}
        <Box round="100%" background={color} width={{min: '10px'}} height={{min: '10px'}} />
        <TextAlignRight>{name}</TextAlignRight>
    </Box>
);

const LineInDrop = ({color, label, minorAmount}) => (
    <Box direction="row" align="center" gap="medium" justify="between">
        <Box direction="row" align="center" gap="xsmall">
            {color && (<Box round="100%" background={color} width={{min: '10px'}} height={{min: '10px'}} />)}
            <Text>{label}</Text>
        </Box>
        <Box>
            {formatCurrency(minorAmount)}
        </Box>
    </Box>
);

const AgingBalanceTable = ({data, currency, generated_on, report_date, onDateChange, date}) => {
    const [sortDirection, setSortDirection] = useState('asc');
    const notificationCtx = useContext(NotificationsContext);
    const onCurrencySort = (field) => ((a, b) => {
        // we want null values to always be at the bottom
        if (typeof a[field] === "undefined") return (sortDirection === 'asc' ? 1 : -1);
        if (typeof b[field] === "undefined") return (sortDirection === 'asc' ? -1 : 1);
        if (a[field] > b[field]) return 1;
        if (a[field] < b[field]) return -1;
        return 0;
    });
    const router = useRouter();
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    const columns = isMobile ? [
        {
            selector: row => row.id,
            cell: (datum) => {
                return (
                    <DropButton
                        event={{section: 'aging_balance_table', element: 'row', description: row.id}}
                        fill="horizontal"
                        focusIndicator={false}
                        plain
                        label={(
                            <Box fill="horizontal" justify="between" direction="row" gap="small" align="center" pad={{right: 'xsmall'}}>
                                <Box>
                                    <Text>{datum?.company_name || formatName(datum, true)}</Text>
                                </Box>
                                <Box>
                                    <Text wordBreak="keep-all">{formatCurrency(datum?.total)}</Text>
                                </Box>
                            </Box>
                        )}
                        dropContent={(
                            <Box pad="medium" border width={{max: 'medium'}} gap="medium" direction="column">
                                <Box pad={{vertical: 'xsmall'}} border={{side: 'bottom'}}>
                                    <LineInDrop label="Total" minorAmount={datum?.total} />
                                </Box>
                                <LineInDrop label="Not yet due" color={AGING_BALANCE_COLOR_MAP['<0']} minorAmount={datum?.not_due} />
                                <LineInDrop label="1-30 days" color={AGING_BALANCE_COLOR_MAP['0+']} minorAmount={datum?.between_1_and_30_days} />
                                <LineInDrop label="31-60 days" color={AGING_BALANCE_COLOR_MAP['30+']} minorAmount={datum?.between_31_and_60_days} />
                                <LineInDrop label="61-90 days" color={AGING_BALANCE_COLOR_MAP['60+']} minorAmount={datum?.between_61_and_90_days} />
                                <LineInDrop label="90+ days" color={AGING_BALANCE_COLOR_MAP['90+']} minorAmount={datum?.over_91_days} />
                                <Button
                                    event={{section: 'aging_balance_table', element: 'view_profile'}}
                                    primary
                                    ctaColor="accent"
                                    color="accent"
                                    fill="horizontal"
                                    label={(
                                        <Box direction="row" gap="small" align="center" fill="horizontal" justify="center">
                                            <Text color="text">View profile</Text>
                                        </Box>
                                    )}
                                    onClick={() => router.push(`/customers/${datum?.id}`)}
                                />
                            </Box>
                        )}
                        dropAlign={{top: 'bottom', left: 'left'}}
                    />
                )
            }
        }
    ]: [
        {
            selector: row => row.company_name,
            name: 'Customer',
            sortable: true,
            sortField: 'company_name',
            grow: 2,
            cell: (datum) => (
                <CustomerTag id={datum.id} inline />
            )
        },
        {
            selector: row => row.not_due,
            name: (<AgingBalanceColumnName color={AGING_BALANCE_COLOR_MAP['<0']} name="Not due" />),
            sortable: true,
            type: 'currency',
            right: true,
            cell: (datum) => datum.not_due ? formatCurrency(datum.not_due, currency) : null,
            sortFunction: onCurrencySort('not_due')
        },
        {
            selector: row => row.between_1_and_30_days,
            name: (<AgingBalanceColumnName color={AGING_BALANCE_COLOR_MAP['0+']} name="1-30 Days" />),
            sortable: true,
            type: 'currency',
            right: true,
            cell: (datum) => datum.between_1_and_30_days ? formatCurrency(datum.between_1_and_30_days, currency) : null,
            sortFunction: onCurrencySort('between_1_and_30_days')
        },
        {
            selector: row => row.between_31_and_60_days,
            name: (<AgingBalanceColumnName color={AGING_BALANCE_COLOR_MAP['30+']} name="31-60 Days" />),
            sortable: true,
            type: 'currency',
            right: true,
            cell: (datum) => datum.between_31_and_60_days ? formatCurrency(datum.between_31_and_60_days, currency) : null,
            sortFunction: onCurrencySort('between_31_and_60_days')
        },
        {
            selector: row => row.between_61_and_90_days,
            name: (<AgingBalanceColumnName color={AGING_BALANCE_COLOR_MAP['60+']} name="61-90 Days" />),
            sortable: true,
            type: 'currency',
            right: true,
            cell: (datum) => datum.between_61_and_90_days ? formatCurrency(datum.between_61_and_90_days, currency) : null,
            sortFunction: onCurrencySort('between_61_and_90_days')
        },
        {
            selector: row => row.over_91_days,
            name: (<AgingBalanceColumnName color={AGING_BALANCE_COLOR_MAP['90+']} name="91+ Days" />),
            sortable: true,
            type: 'currency',
            right: true,
            cell: (datum) => datum.over_91_days ? formatCurrency(datum.over_91_days, currency) : null,
            sortFunction: onCurrencySort('over_91_days')
        },
        {
            selector: row => row.total,
            name: 'Total',
            type: 'currency',
            right: true,
            sortable: true,
            sortField: 'total',
            cell: (datum) => formatCurrency(datum.total, currency)
        },
    ];
    const getAgingBalanceReportAsCsv = (callback) => {
        const url = `/api/reports/aging-balance/csv?report_date=${(date || new Date()).toISOString()}`;
        const exportError = {type: 'error', message: 'Unable to download csv data. Try again later.'};
        fetch(url, {
            method: 'GET'
        })
            .then(handleFileDownloadErrors)
            .then((data) => {
                data ?
                    downloadBlobAsCsvFile(data, `aging_balance_as_of_${formatddMMyyDate(new Date(date))}_exported_on_${getTodaysDate()}.csv`) :
                    notificationCtx.showNotification(exportError);
                callback && callback();
            })
            .catch(e => notificationCtx.showNotification(exportError));
    };

    return (
        <Box>
            <DataTableComponent
                noTableHead={isMobile}
                data={data}
                columns={columns}
                isServer={false}
                dataName="aging_balance_report"
                rightControls={(
                    <DateInput value={date} onChange={onDateChange} labelPrefix="As of " defaultValue={new Date()} defaultLabel="today"/>
                )}
                dataTableWrapperProps={{pad: {horizontal: '0', vertical: 'xsmall'}}}
                controls={(
                    <Box direction="column" pad={{horizontal: 'small'}} align="start">
                        <Text size="large" weight={500}>{`Aging balance report as of ${formatDate(report_date)}`}</Text>
                    </Box>
                )}
                onExportCSVClicked={() => getAgingBalanceReportAsCsv()}
                showRowSeparators={true}
                onSort={(_, direction) => setSortDirection(direction)}/>
        </Box>
    );
}

export default AgingBalancePage;
